import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Chip, CircularProgress, Divider, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  PersonOff as ChurnIcon,
  EventBusy as ExpiredIcon,
  Undo as RefundIcon,
  CurrencyRupee as RupeeIcon,
  PhoneAndroid as InAppIcon,
  PhonelinkOff as AbandonedIcon,
} from '@mui/icons-material';
import {
  useGetChurnedUsersQuery,
  useGetPlansQuery,
  type ChurnedUser,
} from '../../services/marketplaceApi';
import {
  fmtINR,
  getPeriodRange,
  KpiCard,
  PERIOD_LABELS,
  type Period,
} from './MarketplaceDashboard';

function displayName(row: ChurnedUser) {
  const name = [row.first_name, row.last_name].filter(Boolean).join(' ');
  return name || row.username;
}

function fmtWhen(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

const ChurnedUsers: React.FC = () => {
  const [period, setPeriod] = useState<Period>('last_30');
  const [reason, setReason] = useState<'all' | 'expired' | 'refunded'>('all');
  const [appActivity, setAppActivity] = useState<'all' | 'recent' | 'abandoned'>('all');
  const [plan, setPlan] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const range = useMemo(() => getPeriodRange(period), [period]);
  const { data: plans } = useGetPlansQuery();

  const { data, isLoading, isFetching, isError } = useGetChurnedUsersQuery({
    ...(range ? { churned_after: range.after, churned_before: range.before } : {}),
    ...(reason !== 'all' ? { reason } : {}),
    ...(appActivity !== 'all' ? { app_activity: appActivity } : {}),
    ...(plan ? { plan } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
    page: page + 1,
    page_size: pageSize,
  });

  const summary = data?.summary ?? {
    churned_users: 0,
    expired: 0,
    refunded: 0,
    still_in_app: 0,
    abandoned: 0,
    lifetime_gross: 0,
    lifetime_refunded: 0,
  };
  const rows = data?.results ?? [];

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box mb={3} display="flex" alignItems="center" gap={1}>
        <ChurnIcon color="primary" fontSize="large" />
        <Box>
          <Typography variant="h5" fontWeight={700}>Churned users</Typography>
          <Typography variant="caption" color="text.secondary">
            Ended subscriptions with no current plan. In app means they opened it in the last 7 days on the free version. Abandoned means they have not.
          </Typography>
        </Box>
      </Box>

      <Paper elevation={1} sx={{ p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <ToggleButtonGroup
          size="small"
          value={period}
          exclusive
          onChange={(_, value) => {
            if (!value) return;
            setPeriod(value);
            setPage(0);
          }}
          sx={{ flexWrap: 'wrap' }}
        >
          {(Object.keys(PERIOD_LABELS) as Period[]).map((key) => (
            <ToggleButton key={key} value={key} sx={{ fontSize: '0.72rem', px: 1.5 }}>
              {PERIOD_LABELS[key]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem />

        <Select
          size="small"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value as typeof reason);
            setPage(0);
          }}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">All reasons</MenuItem>
          <MenuItem value="expired">Expired</MenuItem>
          <MenuItem value="refunded">Refunded</MenuItem>
        </Select>

        <Select
          size="small"
          value={appActivity}
          onChange={(e) => {
            setAppActivity(e.target.value as typeof appActivity);
            setPage(0);
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">All app activity</MenuItem>
          <MenuItem value="recent">In app (7 days)</MenuItem>
          <MenuItem value="abandoned">Abandoned</MenuItem>
        </Select>

        <Select
          size="small"
          value={plan}
          onChange={(e) => {
            setPlan(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 160 }}
          displayEmpty
        >
          <MenuItem value="">All plans</MenuItem>
          {(plans ?? []).map((item) => (
            <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
          ))}
        </Select>

        <TextField
          size="small"
          placeholder="Phone, email, or name"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 220 }}
        />

        {(isLoading || isFetching) && <CircularProgress size={20} />}
      </Paper>

      {isError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not fetch churned users — the staff endpoint <code>/staff/marketplace/churn/</code> may not be registered yet.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Churned users"
            value={summary.churned_users.toLocaleString('en-IN')}
            icon={<ChurnIcon />}
            color="#6366f1"
            sub={PERIOD_LABELS[period]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Expired"
            value={summary.expired.toLocaleString('en-IN')}
            icon={<ExpiredIcon />}
            color="#f59e0b"
            sub="Did not buy again"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Refunded"
            value={summary.refunded.toLocaleString('en-IN')}
            icon={<RefundIcon />}
            color="#e91e63"
            sub="Refund ended access"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="In app"
            value={summary.still_in_app.toLocaleString('en-IN')}
            icon={<InAppIcon />}
            color="#0ea5e9"
            sub="Opened in the last 7 days"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Abandoned"
            value={summary.abandoned.toLocaleString('en-IN')}
            icon={<AbandonedIcon />}
            color="#64748b"
            sub="No open in the last 7 days"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label="Lifetime gross"
            value={fmtINR(summary.lifetime_gross)}
            icon={<RupeeIcon />}
            color="#10b981"
            sub={summary.lifetime_refunded ? `Refunded ${fmtINR(summary.lifetime_refunded)}` : 'Paid orders'}
          />
        </Grid>
      </Grid>

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Churned at</TableCell>
              <TableCell>Last active</TableCell>
              <TableCell>App</TableCell>
              <TableCell>Last plan</TableCell>
              <TableCell align="right">Purchases</TableCell>
              <TableCell align="right">Lifetime gross</TableCell>
              <TableCell align="right">Refunded</TableCell>
              <TableCell>Plans bought</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.user} hover>
                <TableCell>
                  <Typography
                    component={RouterLink}
                    to={`/users/${row.user}`}
                    variant="body2"
                    sx={{ fontWeight: 600, textDecoration: 'none', color: 'primary.main' }}
                  >
                    {displayName(row)}
                  </Typography>
                </TableCell>
                <TableCell>{row.username}</TableCell>
                <TableCell>{row.email || '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.reason === 'refunded' ? 'Refunded' : 'Expired'}
                    color={row.reason === 'refunded' ? 'error' : 'warning'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{fmtWhen(row.churned_at)}</TableCell>
                <TableCell>{fmtWhen(row.last_active)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.app_status === 'recent' ? 'In app' : 'Abandoned'}
                    color={row.app_status === 'recent' ? 'info' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {row.plan_name}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {fmtINR(row.plan_price)}
                  </Typography>
                </TableCell>
                <TableCell align="right">{row.purchase_count}</TableCell>
                <TableCell align="right">{fmtINR(row.lifetime_gross)}</TableCell>
                <TableCell align="right">{fmtINR(row.lifetime_refunded)}</TableCell>
                <TableCell>{row.plans_bought.join(', ') || '—'}</TableCell>
              </TableRow>
            ))}
            {!isLoading && !isError && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No churned users in this period.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data?.count ?? 0}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};

export default ChurnedUsers;
